import { Link } from "react-router-dom";
import { DOC_SECTIONS, DOC_TIERS, TIER_META, docPath, type DocTier } from "../../content/docs/docsManifest";

type DocsSidebarProps = {
    activeSlug: string;
    activeTier: DocTier;
};

/**
 * Section switcher plus the Quick Start / Complete Manual toggle. Switching
 * tiers holds the current section so a reader who wants more depth on the page
 * they are already reading lands on that same section's manual.
 */
export default function DocsSidebar({ activeSlug, activeTier }: DocsSidebarProps) {
    return (
        <div className="space-y-6">
            <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Reading</div>
                <div className="flex rounded-lg border border-slate-800 bg-slate-900/60 p-1">
                    {DOC_TIERS.map((tier) => (
                        <Link
                            key={tier}
                            to={docPath(tier, activeSlug)}
                            className={`flex-1 rounded-md px-2 py-1.5 text-center text-xs font-medium transition ${
                                tier === activeTier ? "bg-gradient-to-r from-orange-500 to-pink-500 text-white shadow" : "text-slate-400 hover:text-slate-200"
                            }`}
                        >
                            {TIER_META[tier].label}
                        </Link>
                    ))}
                </div>
            </div>

            <nav aria-label="Documentation sections">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Sections</div>
                <ul className="space-y-0.5">
                    {DOC_SECTIONS.map((section) => {
                        const isActive = section.slug === activeSlug;
                        const Icon = section.icon;
                        return (
                            <li key={section.slug}>
                                <Link
                                    to={docPath(activeTier, section.slug)}
                                    aria-current={isActive ? "page" : undefined}
                                    className={`flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition ${
                                        isActive ? "bg-slate-800/70 font-medium text-white" : "text-slate-400 hover:bg-slate-800/40 hover:text-slate-200"
                                    }`}
                                >
                                    <Icon className={`h-4 w-4 shrink-0 ${isActive ? "text-orange-400" : "text-slate-500"}`} />
                                    <span className="truncate">{section.title}</span>
                                    {section.status === "outline" ? (
                                        <span className="ml-auto shrink-0 rounded border border-slate-700 px-1 text-[10px] uppercase tracking-wide text-slate-500">
                                            Outline
                                        </span>
                                    ) : null}
                                </Link>
                            </li>
                        );
                    })}
                </ul>
            </nav>

            <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 text-xs text-slate-400">
                <div className="mb-1 font-medium text-slate-300">Integration guides</div>
                <Link to="/docs/shopify" className="text-orange-400 underline decoration-orange-400/40 underline-offset-2 hover:text-orange-300">
                    FloGPT for Shopify
                </Link>
            </div>
        </div>
    );
}
